import { inject, Injectable } from '@angular/core';

import {
	DEFAULT_DUPLICATE_REQUEST_WINDOW_MS,
	DEFAULT_MAX_TRACKED_REQUESTS,
} from '../config/duplicate-request-monitoring.constants';
import { DUPLICATE_REQUEST_MONITORING_OPTIONS } from '../config/duplicate-request-monitoring-options.token';
import { DuplicateRequestCandidate } from '../contracts/duplicate-request-candidate.interface';
import { DuplicateRequestMatch, DuplicateRequestReason } from '../contracts/duplicate-request-match.interface';

function isWithinWindow(
	previous: DuplicateRequestCandidate,
	current: DuplicateRequestCandidate,
	windowMs: number,
): boolean {
	return current.timestamp >= previous.timestamp && current.timestamp - previous.timestamp <= windowMs;
}

function isSubset(currentKeys: readonly string[], previousKeys: readonly string[]): boolean {
	const previousKeysSet = new Set(previousKeys);

	return currentKeys.every(key => previousKeysSet.has(key));
}

function shouldReportSubsetDuplicate(previous: DuplicateRequestCandidate, current: DuplicateRequestCandidate): boolean {
	if (previous.fingerprint === current.fingerprint) {
		return false;
	}

	if (current.payloadShape.isEmpty) {
		return true;
	}

	if (!isSubset(current.payloadShape.keys, previous.payloadShape.keys)) {
		return false;
	}

	return current.payloadShape.keys.length < previous.payloadShape.keys.length || previous.payloadShape.hasId;
}

function evictStale(
	map: Map<string, DuplicateRequestCandidate>,
	nowTimestamp: number,
	windowMs: number,
	maxSize: number,
): void {
	if (map.size <= maxSize) {
		return;
	}

	for (const [key, candidate] of map) {
		if (nowTimestamp - candidate.timestamp > windowMs) {
			map.delete(key);
		}
	}

	if (map.size <= maxSize) {
		return;
	}

	const toRemove = map.size - Math.floor(maxSize / 2);
	let removed = 0;
	for (const key of map.keys()) {
		if (removed >= toRemove) {
			break;
		}
		map.delete(key);
		removed += 1;
	}
}

@Injectable({ providedIn: 'root' })
export class DuplicateRequestDetectorService {
	private readonly options = inject(DUPLICATE_REQUEST_MONITORING_OPTIONS);
	private readonly requestsByFingerprint = new Map<string, DuplicateRequestCandidate>();
	private readonly requestsByOperation = new Map<string, DuplicateRequestCandidate>();

	public track(candidate: DuplicateRequestCandidate): DuplicateRequestMatch | null {
		const windowMs = this.options.duplicateWindowMs ?? DEFAULT_DUPLICATE_REQUEST_WINDOW_MS;
		const maxTracked = this.options.maxTrackedRequests ?? DEFAULT_MAX_TRACKED_REQUESTS;
		const exactPrevious = this.requestsByFingerprint.get(candidate.fingerprint);
		const operationPrevious = candidate.sameOperationKey
			? this.requestsByOperation.get(candidate.sameOperationKey)
			: undefined;

		this.requestsByFingerprint.set(candidate.fingerprint, candidate);

		if (candidate.sameOperationKey) {
			this.requestsByOperation.set(candidate.sameOperationKey, candidate);
		}

		evictStale(this.requestsByFingerprint, candidate.timestamp, windowMs, maxTracked);
		evictStale(this.requestsByOperation, candidate.timestamp, windowMs, maxTracked);

		if (exactPrevious && isWithinWindow(exactPrevious, candidate, windowMs)) {
			return this.createMatch('same-fingerprint', exactPrevious, candidate);
		}

		if (!candidate.sameOperationKey) {
			return null;
		}

		if (!operationPrevious || !isWithinWindow(operationPrevious, candidate, windowMs)) {
			return null;
		}

		if (!shouldReportSubsetDuplicate(operationPrevious, candidate)) {
			return null;
		}

		return this.createMatch('empty-or-subset-payload', operationPrevious, candidate);
	}

	private createMatch(
		reason: DuplicateRequestReason,
		previous: DuplicateRequestCandidate,
		current: DuplicateRequestCandidate,
	): DuplicateRequestMatch {
		return {
			reason,
			gapMs: current.timestamp - previous.timestamp,
			previous,
			current,
		};
	}
}
